<section class="managing-utility-page">
  <div class="page-shell">
    <header class="page-header app-card">
      <div>
        <h1>{{ title() }}</h1>
        <span>{{ description() }}</span>
      </div>
    </header>

    <div class="stats-grid">
      @for (card of stats(); track card.label) {
        <article class="stat-card app-card" [class]="'tone-' + card.tone">
          <div class="stat-icon">{{ card.icon }}</div>
          <div class="stat-copy">
            <span>{{ card.label }}</span>
            <strong>{{ card.value }}</strong>
          </div>
        </article>
      }
    </div>

    <section class="table-card app-card">
      <div class="filter-grid">
        <label class="filter-field">
          <span>Search Utility</span>
          <input
            class="app-form-input"
            type="search"
            placeholder="Enter utility code / name"
            [value]="searchTerm()"
            (input)="updateSearch($any($event.target).value)"
          >
        </label>

        <label class="filter-field">
          <span>Category</span>
          <select class="app-form-select" [value]="selectedCategory()" (change)="updateCategory($any($event.target).value)">
            @for (option of filters().category; track option) {
              <option [value]="option">{{ option }}</option>
            }
          </select>
        </label>

        <label class="filter-field">
          <span>State</span>
          <select class="app-form-select" [value]="selectedState()" (change)="updateState($any($event.target).value)">
            @for (option of filters().state; track option) {
              <option [value]="option">{{ option }}</option>
            }
          </select>
        </label>

        <label class="filter-field">
          <span>Status</span>
          <select class="app-form-select" [value]="selectedStatus()" (change)="updateStatus($any($event.target).value)">
            @for (option of filters().status; track option) {
              <option [value]="option">{{ option }}</option>
            }
          </select>
        </label>

        <div class="filter-actions">
          <button type="button" class="app-button app-button--primary">Search</button>
          <button type="button" class="app-button app-button--secondary" (click)="resetFilters()">Reset</button>
        </div>
      </div>

      <div class="table-wrap">
        <table class="utility-table">
          <thead>
            <tr>
              <th>Utility Code</th>
              <th>Utility Name</th>
              <th>Category</th>
              <th>State</th>
              <th>Mapped Units</th>
              <th>Mapped PSS</th>
              <th>Mapped Generating Companies</th>
              <th>Contract Count</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            @for (row of paginatedRows(); track row.code) {
              <tr>
                <td><a href="" (click)="$event.preventDefault()">{{ row.code }}</a></td>
                <td>{{ row.name }}</td>
                <td>{{ row.category }}</td>
                <td>{{ row.state }}</td>
                <td>{{ row.mappedUnitCount }}</td>
                <td>{{ row.mappedPssCount }}</td>
                <td>{{ row.mappedCompanyCount }}</td>
                <td>{{ row.contractCount }}</td>
                <td>
                  <span class="status-pill" [class.inactive]="row.status === 'Inactive'">{{ row.status }}</span>
                </td>
                <td>
                  <button type="button" class="manage-button" (click)="openManageMappingModal(row.code)">Manage Units</button>
                </td>
              </tr>
            } @empty {
              <tr>
                <td colspan="10" class="empty-state">No utility records found for the selected filters.</td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      <footer class="table-footer">
        <span>{{ showingText() }}</span>

        <div class="pagination">
          <button type="button" class="page-button" [disabled]="currentPage() === 1" (click)="goToPage(currentPage() - 1)">‹</button>
          @for (page of pageNumbers(); track page) {
            <button type="button" class="page-button" [class.active]="page === currentPage()" (click)="goToPage(page)">
              {{ page }}
            </button>
          }
          <button type="button" class="page-button" [disabled]="currentPage() === totalPages()" (click)="goToPage(currentPage() + 1)">›</button>
        </div>
      </footer>
    </section>

    @if (isManageMappingModalOpen() && selectedUtility()) {
      <div class="modal-backdrop" role="presentation">
        <div class="manage-mapping-modal app-card" role="dialog" aria-modal="true" aria-labelledby="manage-mapping-title">
          <div class="modal-header">
            <div>
              <h2 id="manage-mapping-title">Manage Mapping for {{ selectedUtility()?.code }} - {{ selectedUtility()?.name }}</h2>
            </div>
            <button type="button" class="modal-close" (click)="closeManageMappingModal()" aria-label="Close manage mapping modal">×</button>
          </div>

          <div class="modal-tabs">
            <button type="button" class="tab-button" [class.active]="activeUnitTab() === 'available'" (click)="switchUnitTab('available')">
              Available Units
            </button>
            <button type="button" class="tab-button" [class.active]="activeUnitTab() === 'mapped'" (click)="switchUnitTab('mapped')">
              Mapped Units ({{ mappedUnits().length }})
            </button>
          </div>

          <div class="modal-body">
            @if (activeUnitTab() === 'available') {
              <div class="modal-toolbar">
                <label class="modal-search">
                  <input
                    class="app-form-input"
                    type="search"
                    placeholder="Search available units..."
                    [value]="availableUnitSearch()"
                    (input)="updateAvailableUnitSearch($any($event.target).value)"
                  >
                </label>

                <button type="button" class="add-selected-button" [disabled]="selectedAvailableUnitCodes().length === 0" (click)="addSelectedUnits()">
                  Add Selected
                </button>
              </div>

              <div class="modal-table-wrap">
                <table class="modal-table">
                  <thead>
                    <tr>
                      <th></th>
                      <th>PSS</th>
                      <th>Generating Company</th>
                      <th>Generator</th>
                      <th>Unit</th>
                      <th>Contract Type</th>
                      <th>Contracted Capacity (MW)</th>
                      <th>Contract Period</th>
                      <th>OA / Buyer Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (item of filteredAvailableUnits(); track item.unitCode) {
                      <tr>
                        <td>
                          <input
                            type="checkbox"
                            [checked]="selectedAvailableUnitCodes().includes(item.unitCode)"
                            (change)="toggleAvailableSelection(item.unitCode, $any($event.target).checked)"
                          >
                        </td>
                        <td>{{ item.pssName }}</td>
                        <td>{{ item.generatingCompanyName }}</td>
                        <td>{{ item.generatorName }}</td>
                        <td>{{ item.unitName }}</td>
                        <td>{{ item.contractType }}</td>
                        <td>{{ formatCapacity(item.contractedCapacityMw) }}</td>
                        <td>{{ item.contractPeriod }}</td>
                        <td>{{ item.oaBuyerType }}</td>
                      </tr>
                    } @empty {
                      <tr>
                        <td colspan="9" class="empty-state">No available unit records found.</td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            } @else {
              <div class="modal-toolbar">
                <label class="modal-search">
                  <input
                    class="app-form-input"
                    type="search"
                    placeholder="Search mapped units..."
                    [value]="mappedUnitSearch()"
                    (input)="updateMappedUnitSearch($any($event.target).value)"
                  >
                </label>

                <button type="button" class="remove-selected-button" [disabled]="selectedMappedUnitCodes().length === 0" (click)="removeSelectedUnits()">
                  Remove Selected
                </button>
              </div>

              <div class="modal-table-wrap">
                <table class="modal-table">
                  <thead>
                    <tr>
                      <th></th>
                      <th>PSS</th>
                      <th>Generating Company</th>
                      <th>Generator</th>
                      <th>Unit</th>
                      <th>Contract Type</th>
                      <th>Contracted Capacity (MW)</th>
                      <th>Contract Period</th>
                      <th>OA / Buyer Type</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (item of filteredMappedUnits(); track item.unitCode) {
                      <tr>
                        <td>
                          <input
                            type="checkbox"
                            [checked]="selectedMappedUnitCodes().includes(item.unitCode)"
                            (change)="toggleMappedSelection(item.unitCode, $any($event.target).checked)"
                          >
                        </td>
                        <td>{{ item.pssName }}</td>
                        <td>{{ item.generatingCompanyName }}</td>
                        <td>{{ item.generatorName }}</td>
                        <td>{{ item.unitName }}</td>
                        <td>{{ item.contractType }}</td>
                        <td>{{ formatCapacity(item.contractedCapacityMw) }}</td>
                        <td>{{ item.contractPeriod }}</td>
                        <td>{{ item.oaBuyerType }}</td>
                        <td>
                          <button type="button" class="remove-icon-button" (click)="removeMappedUnit(item.unitCode)" aria-label="Remove mapped unit">🗑</button>
                        </td>
                      </tr>
                    } @empty {
                      <tr>
                        <td colspan="10" class="empty-state">No mapped unit records found.</td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            }
          </div>
        </div>
      </div>
    }
  </div>
</section>
